'use client'
import type { Breakpoint } from '@mui/material/styles'

import { mergeClasses } from 'minimal-shared/utils'

import { imageClasses } from './classes'

import { forwardRef } from 'react'
import { EffectsType, ImageImg, ImageRoot } from './styles'

// ----------------------------------------------------------------------

type AspectRatioType =
  | '2/3'
  | '3/2'
  | '4/3'
  | '3/4'
  | '6/4'
  | '4/6'
  | '16/9'
  | '9/16'
  | '21/9'
  | '9/21'
  | '1/1'
  | string

type ImageProps = React.ComponentProps<typeof ImageRoot> & {
  src?: string
  alt?: string
  delayTime?: number
  onLoad?: () => void
  effect?: EffectsType
  visibleByDefault?: boolean
  disablePlaceholder?: boolean
  ratio?: AspectRatioType | Partial<Record<Breakpoint, AspectRatioType>>
  slotProps?: {
    img?: Omit<React.ComponentProps<typeof ImageImg>, 'src' | 'alt'>
  }
}

export const Image = forwardRef<HTMLSpanElement, ImageProps>((props, ref) => {
  const {
    sx,
    src,
    ratio,
    onLoad,
    effect,
    alt = '',
    className,
    disablePlaceholder,
    visibleByDefault = false,
    slotProps,
    ...other
  } = props

  return (
    <ImageRoot
      className={mergeClasses([imageClasses.root, className], {
        [imageClasses.state.loaded]: !visibleByDefault,
      })}
      sx={[
        {
          '--aspect-ratio': ratio,
          ...(!!ratio && { width: 1 }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <ImageImg src={src} alt={alt} className={imageClasses.img} {...slotProps?.img} />
    </ImageRoot>
  )
})

Image.displayName = 'Image'
